import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Calendar,
  UserCircle,
  PencilSimple,
  Trash,
  ArrowLeft,
  Plus
} from '@phosphor-icons/react';
import { Epic, EpicStatus, EpicProgress } from 'shared/types/epic';
import { useUsers } from 'shared/contexts/users.context';
import { format, isValid } from 'date-fns';
import { nl } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

interface EpicDetailHeaderProps {
  epic: Epic;
  progress: EpicProgress;
  onEdit: () => void;
  onDelete: () => void;
  onCreateFeature: () => void;
}

export const EpicDetailHeader: React.FC<EpicDetailHeaderProps> = ({
  epic,
  progress,
  onEdit,
  onDelete,
  onCreateFeature
}) => {
  const { users } = useUsers();
  const navigate = useNavigate();

  const getUserName = (userId: string) => {
    const foundUser = users.find((user) => user.id === userId);
    return foundUser
      ? `${foundUser.first_name} ${foundUser.last_name}`
      : 'Onbekend';
  };

  const statusConfig = {
    [EpicStatus.PLANNING]: {
      color: 'text-blue-700',
      bgColor: 'bg-blue-100',
      label: 'Planning'
    },
    [EpicStatus.IN_PROGRESS]: {
      color: 'text-moss-primary',
      bgColor: 'bg-moss-primary/10',
      label: 'In Behandeling'
    },
    [EpicStatus.COMPLETED]: {
      color: 'text-status-good',
      bgColor: 'bg-status-good/10',
      label: 'Afgerond'
    },
    [EpicStatus.ON_HOLD]: {
      color: 'text-orange-700',
      bgColor: 'bg-orange-100',
      label: 'On Hold'
    }
  };

  const config = statusConfig[epic.status];

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Niet ingesteld';
    const date = new Date(dateString);
    return isValid(date) ? format(date, 'd MMMM yyyy', { locale: nl }) : 'Ongeldig';
  };

  return (
    <div className="space-y-4">
      {/* Back Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/bug-reports/epics')}
      >
        <ArrowLeft size={16} className="mr-2" />
        Terug naar Epics
      </Button>

      <Card className="p-6">
        <div className="space-y-6">
          {/* Header Row */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold">{epic.title}</h1>
                <Badge
                  className={`${config.bgColor} ${config.color} border-0`}
                >
                  {config.label}
                </Badge>
              </div>
              <div className="text-sm text-neutral-500">EPIC-{epic.id}</div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={onCreateFeature}
                className="bg-moss-primary hover:bg-moss-primary/90"
              >
                <Plus size={16} className="mr-2" />
                Nieuwe Feature
              </Button>
              <Button variant="outline" size="sm" onClick={onEdit}>
                <PencilSimple size={16} className="mr-2" />
                Bewerken
              </Button>
              <Button variant="destructive" size="sm" onClick={onDelete}>
                <Trash size={16} className="mr-2" />
                Verwijderen
              </Button>
            </div>
          </div>

          {/* Description */}
          {epic.description && (
            <div className="text-neutral-700">{epic.description}</div>
          )}

          {/* Meta Info Grid */}
          <div className="grid grid-cols-3 gap-6">
            {/* Date Range */}
            <div>
              <div className="text-xs text-neutral-500 mb-1">Periode</div>
              <div className="flex items-center gap-2 text-sm">
                <Calendar size={16} className="text-neutral-400" />
                <span>
                  {formatDate(epic.startDate)} - {formatDate(epic.endDate)}
                </span>
              </div>
            </div>

            {/* Owner */}
            <div>
              <div className="text-xs text-neutral-500 mb-1">Eigenaar</div>
              <div className="flex items-center gap-2 text-sm">
                <UserCircle
                  size={16}
                  weight="fill"
                  className="text-moss-primary"
                />
                <span className="text-moss-primary font-medium">
                  {epic.ownerId ? getUserName(epic.ownerId) : 'Geen eigenaar'}
                </span>
              </div>
            </div>

            {/* Feature Count */}
            <div>
              <div className="text-xs text-neutral-500 mb-1">Features</div>
              <div className="text-sm font-semibold">
                {progress.total} {progress.total === 1 ? 'feature' : 'features'}
              </div>
            </div>
          </div>

          {/* Progress Section */}
          {progress.total > 0 && (
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm">Voortgang</h3>
                <span className="text-2xl font-bold text-moss-primary">
                  {progress.percentComplete}%
                </span>
              </div>

              <Progress value={progress.percentComplete} className="h-3" />

              {/* Status Breakdown */}
              <div className="grid grid-cols-5 gap-4 text-xs">
                <div className="text-center">
                  <div className="text-blue-700 font-semibold">
                    {progress.reported}
                  </div>
                  <div className="text-neutral-500">Gemeld</div>
                </div>
                <div className="text-center">
                  <div className="text-moss-primary font-semibold">
                    {progress.inProgress}
                  </div>
                  <div className="text-neutral-500">In Behandeling</div>
                </div>
                <div className="text-center">
                  <div className="text-purple-700 font-semibold">
                    {progress.testing}
                  </div>
                  <div className="text-neutral-500">Testing</div>
                </div>
                <div className="text-center">
                  <div className="text-status-good font-semibold">
                    {progress.resolved}
                  </div>
                  <div className="text-neutral-500">Opgelost</div>
                </div>
                <div className="text-center">
                  <div className="text-neutral-600 font-semibold">
                    {progress.closed}
                  </div>
                  <div className="text-neutral-500">Gesloten</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
